/**
 * useCountryDetection Hook
 * Smart country detection from phone prefix and value management
 *
 * Features:
 * - Auto-detect country from phone prefix (e.g., +62 → Indonesia)
 * - Preserve national number when country changes
 * - Initial country detection from value or browser locale
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type { CountryCode } from 'libphonenumber-js';

import type { UseCountryDetectionReturn } from '../types';
import {
  detectCountryFromValue,
  extractNationalNumber,
  buildPhoneValue,
  getCallingCode,
} from '../utils/phoneParser';
import { detectUserCountry } from '../utils/countryMetadata';

interface UseCountryDetectionOptions {
  /** Initial value (may contain country code) */
  initialValue?: string;
  /** Explicitly provided default country */
  defaultCountry?: CountryCode;
  /** Whether to auto-detect from browser locale */
  autoDetectCountry?: boolean;
  /** Callback when country changes */
  onCountryChange?: (country: CountryCode) => void;
}

export function useCountryDetection({
  initialValue = '',
  defaultCountry,
  autoDetectCountry = true,
  onCountryChange,
}: UseCountryDetectionOptions = {}): UseCountryDetectionReturn {
  /**
   * Resolve initial country:
   * 1. From initial value (if it contains country code)
   * 2. From explicitly provided defaultCountry
   * 3. From browser locale (if autoDetectCountry)
   * 4. Fallback to 'US'
   */
  const resolvedInitialCountry = useMemo((): CountryCode => {
    // Priority 1: Extract from initial value
    if (initialValue) {
      const detected = detectCountryFromValue(initialValue);
      if (detected) return detected;
    }

    // Priority 2: Use explicit default
    if (defaultCountry) return defaultCountry;

    // Priority 3: Auto-detect from browser
    if (autoDetectCountry) return detectUserCountry();

    // Fallback
    return 'US';
  }, [initialValue, defaultCountry, autoDetectCountry]);

  const [country, setCountryState] = useState<CountryCode>(resolvedInitialCountry);

  // Track if this is initial mount to avoid unnecessary callbacks
  const isInitialMount = useRef(true);

  /**
   * Set country with callback notification
   */
  const setCountry = useCallback(
    (newCountry: CountryCode) => {
      setCountryState((prev) => {
        if (prev !== newCountry) {
          // Defer callback to avoid calling during render
          setTimeout(() => onCountryChange?.(newCountry), 0);
          return newCountry;
        }
        return prev;
      });
    },
    [onCountryChange]
  );

  /**
   * Handle value change and detect country from prefix
   *
   * Smart detection rules:
   * - Only detect when value starts with '+'
   * - Wait for unambiguous prefix (e.g., +62 not +6)
   * - Don't change country if user manually selected one recently
   */
  const handleValueChange = useCallback(
    (value: string) => {
      if (!value.startsWith('+')) return;

      const detected = detectCountryFromValue(value);
      if (detected && detected !== country) {
        setCountry(detected);
      }
    },
    [country, setCountry]
  );

  /**
   * Handle country change from dropdown
   * Preserves the national number portion of the phone value
   *
   * @param newCountry - The newly selected country
   * @param currentValue - The current phone value
   * @returns The new phone value with updated country code
   */
  const handleCountryChange = useCallback(
    (newCountry: CountryCode, currentValue: string): string => {
      // Extract national number from current value
      const nationalNumber = extractNationalNumber(currentValue, country);

      // Update country state
      setCountry(newCountry);

      // Build new value with new country code
      if (nationalNumber) {
        return buildPhoneValue(newCountry, nationalNumber);
      }

      // If no national number, just return the country code prefix
      const callingCode = getCallingCode(newCountry);
      return callingCode ? `+${callingCode}` : '';
    },
    [country, setCountry]
  );

  /**
   * Skip initial mount callback
   */
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, []);

  return {
    country,
    setCountry,
    handleValueChange,
    handleCountryChange,
  };
}
